import type { Context } from "@apollo/client";

export interface GraphqlErrorContext extends Context {
  httpCode: number | undefined;
  errorMessage: string | undefined;
  operationName: string;
  graphqlErrors: string | null;
  /** `extensions.code` of every error of the response, see `graphqlErrorCodes` */
  errorCodes: string[];
  queries: { type: string; query: string | undefined }[];
}

/**
 * Contexts of GraphQL failures, keyed by the error objects the apollo link chain has seen.
 *
 * The link chain is the only place that knows which operation failed, but the same failure is reported to Sentry
 * a second time from outside the chain (error boundary, `captureError`), where the operation name is not
 * available - such events used to carry no GraphQL context at all. Errors are held weakly, so remembering them
 * cannot keep failed operations alive.
 */
const contexts = new WeakMap<object, GraphqlErrorContext>();

const isObject = (value: unknown): value is object => typeof value === "object" && value !== null;

/**
 * Find the context of a GraphQL failure for an error captured outside the link chain.
 *
 * Apollo hands the component an `ApolloError` wrapping the very objects the link chain saw, so the lookup tries
 * the error itself, its network error and its GraphQL errors.
 */
export const findGraphqlErrorContext = (error: unknown): GraphqlErrorContext | undefined => {
  if (!isObject(error)) {
    return undefined;
  }
  const direct = contexts.get(error);
  if (direct) {
    return direct;
  }
  const { networkError, graphQLErrors } = error as { networkError?: unknown; graphQLErrors?: unknown };
  if (isObject(networkError) && contexts.has(networkError)) {
    return contexts.get(networkError);
  }
  if (!Array.isArray(graphQLErrors)) {
    return undefined;
  }
  return graphQLErrors
    .map((graphqlError) => (isObject(graphqlError) ? contexts.get(graphqlError) : undefined))
    .find(Boolean);
};

/**
 * Remember the context of a GraphQL failure for every error object that can reach a later Sentry capture
 */
export const rememberGraphqlErrorContext = (
  context: GraphqlErrorContext,
  errors: readonly unknown[]
): GraphqlErrorContext => {
  errors.filter(isObject).forEach((error) => contexts.set(error, context));
  return context;
};
